<template>
  <div
    class="kk-tree__title"
    :class="{
      'kk-tree__title-active': isActive && scopeOnSidebar,
      'kk-tree__title-disabled': disabled,
      'kk-tree__title-not_focused': isActive && !scopeOnSidebar,
    }"
    :style="{ 'padding-left': paddingLeft }"
    :data-id="id"
    :item-id="itemId"
    :data-test-id="testId"
    :data-title="item.title"
    :draggable="draggable"
    @dragstart="$emit('drag-start-called', $event)"
    @dragover="$emit('drag-over-called', $event)"
    @drop="$emit('drop-called', $event)"
    @dragleave="$emit('drag-leave-called', $event)"
    @dragenter="$emit('drag-enter-called', $event)"
  >
    <custom-icon
      :class-name="item.expanded ? icons.ChevronDown : icons.ChevronRight"
      :icon-color="iconColor"
      :class="{ 'kk-tree__icon-hidden': item.childrenCount === 0 }"
    />
    <custom-icon :transition="undefined" :icon-color="iconColor" :class-name="` ${icon} kk-tree__icon`" />
    <input
      ref="input"
      v-model="localTitle"
      class="kk-tree__title-text"
      :readonly="!editable"
      @blur="saveData"
      @keydown.enter.prevent="enter"
      @mousedown="handleMouseDown"
    />
    <div v-if="currentItem" :class="`kk-tree__title-current ${icons.CheckBold}`"></div>
    <div v-if="item.shared" :class="`kk-tree__title-current ${icons.Share}`"></div>
  </div>
</template>

<script>
import { HotkeyScopes } from "../../../utils/constants";
import { icons } from "../../../utils/icons";
import CustomIcon from "../../commons/CustomIcon.vue";
import DesignerMixin from "../../process-design/DesignerMixin";
export default {
  components: { CustomIcon },
  mixins: [DesignerMixin],
  props: [
    "id",
    "itemId",
    "editable",
    "isActive",
    "depth",
    "draggable",
    "currentItem",
    "hashTable",
    "testId",
    "disabled",
  ],
  emits: [
    "drag-start-called",
    "drag-over-called",
    "drop-called",
    "drag-leave-called",
    "drag-enter-called",
    "save-data",
  ],
  data() {
    return { localTitle: "", icons: icons };
  },
  computed: {
    item() {
      return this.hashTable[this.id] || {};
    },
    paddingLeft() {
      return this.depth === 0 ? "0.4rem" : `${+this.depth * 20}px`;
    },
    iconColor() {
      return this.isActive && this.scopeOnSidebar ? "#fff" : "#083277";
    },
    icon() {
      return this.item.type ? icons[this.item.type.toLowerCase()] ?? icons.SHEETS : null;
    },
    scopeOnSidebar() {
      return [
        HotkeyScopes.Sidebar.Options,
        HotkeyScopes.Sidebar.Players,
        HotkeyScopes.Sidebar.Processes,
        HotkeyScopes.Sidebar.Grids,
        HotkeyScopes.Sidebar.Businesses,
        HotkeyScopes.Sidebar.Teams,
        HotkeyScopes.SidebarContextMenu,
      ].includes(this.$store.state.activeScope);
    },
  },
  watch: {
    editable(newVal) {
      if (newVal) {
        this.selectText();
      }
    },
    item(newVal, oldVal) {
      if (newVal.title !== oldVal.title) this.localTitle = newVal.title;
    },
  },
  mounted() {
    if (this.editable) {
      this.selectText();
    }
    this.$nextTick(() => {
      this.localTitle = this.item?.title;
    });
  },
  methods: {
    selectText() {
      this.$nextTick(() => {
        const editingElement = this.$refs.input;
        editingElement.focus();
        editingElement.select();
      });
    },
    enter(e) {
      e.target.blur();
    },
    saveData() {
      window.getSelection()?.removeAllRanges();
      this.localTitle = this.localTitle?.trim();

      if (this.localTitle === this.item.title) return;
      if (this.localTitle.length === 0 || (this.item.type === "sheet" && this.nameExists()))
        return (this.localTitle = this.item.title);

      this.$emit("save-data", this.localTitle);
    },
    nameExists() {
      const check = this.allSheets.some((x) => x.name.toLowerCase() === this.localTitle.toLowerCase());
      if (check)
        this.$store.dispatch("addToast", {
          severity: "error",
          life: 3000,
          summary: "This name already exists",
        });
      return check;
    },
    handleMouseDown(e) {
      if (e.detail > 1) {
        e.preventDefault(); // Prevent default text selection behavior
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";
.kk-tree {
  &__title {
    & input {
      color: @kk-color-primary-blue;
    }
    padding: 0.2rem;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    height: 40px;
    &-text {
      flex-grow: 1;
      flex-basis: 0;
      min-width: 2rem;
      cursor: pointer;
      &:focus-visible {
        outline: none;
      }
    }
    &:not(&-active)&:hover,
    &-not_focused {
      background-color: @kk-color-light-blue;
    }
    &-active {
      background-color: @kk-color-primary-blue;
      & input,
      .kk-tree__title-current {
        color: #fff;
      }
    }
    &-current {
      color: @kk-color-primary-blue;
    }
    &-center {
      background-color: @kk-color-lighter-gray;
    }
    &-disabled {
      & input,
      .kk-tree__icon {
        color: @kk-color-gray-1 !important;
      }
    }
  }
  &__icon {
    padding-right: 4px;
  }
  &__icon-hidden {
    visibility: hidden;
  }
}
</style>
